import { useMemo } from 'react';
import { Equipment, Project } from '../types/project';
import { Trash2, Plus } from 'lucide-react';
import { Tooltip } from './Tooltip';
import {
  DWELLING_CHECKLIST,
  CHECKLIST_GROUP_LABELS,
  DwellingChecklistItem,
  rowForChecklistItem,
  quantityOptions,
} from '../lib/dwellingDefaults';
import { defaultEditionForState, getEditionPack } from '../lib/calc/editions';
import { clampInput } from '../lib/numericInput';

// The traditional path's input surface (docs/specs/traditional-calcs.md §5.3):
// floor area + branch-circuit counts, an appliance checklist that seeds
// equipment rows with load_role 'existing', and the inventory attestation.
// Everything here is traditional-only — the measured path renders none of it,
// and nothing here is required for a measured project.

interface DwellingProfileProps {
  project: Partial<Project>;
  /** Only rows with load_role 'existing' — the caller filters. */
  existingRows: Equipment[];
  onChange: (updates: Partial<Project>) => void;
  onAddExisting: (seed: Partial<Equipment>) => void;
  /** Turns a row of N identical units into N rows of one. Owned by App: it is a
   *  single state write, so it cannot be assembled from add + update here. */
  onSplitExisting: (id: string) => void;
  /** Moves the row to the New Loads tab's removed list. Absent in the demo. */
  onMarkRemoved?: (id: string) => void;
  /** Rows with load_role 'removed'. They stay listed here, grayed out and
   *  flagged, so the inventory still shows the whole house as it is today;
   *  none of them counts. */
  removedRows?: Equipment[];
  /** Puts a removed row back in the inventory (load_role 'existing'). */
  onReturnToInventory?: (id: string) => void;
  onUpdateEquipment: (id: string, updates: Partial<Equipment>) => void;
  onDeleteEquipment: (id: string) => void;
  isReadOnly?: boolean;
}

const inputClass =
  'w-full px-3 py-2.5 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 disabled:cursor-not-allowed text-base';

function rowVA(eq: Equipment): number {
  const qty = eq.quantity > 0 ? eq.quantity : 1;
  if (eq.kw !== null) return eq.kw * 1000 * qty;
  if (eq.amps !== null) return eq.amps * eq.voltage * qty;
  return 0;
}

export function DwellingProfile({
  project,
  existingRows,
  onChange,
  onAddExisting,
  onSplitExisting,
  onMarkRemoved,
  removedRows = [],
  onReturnToInventory,
  onUpdateEquipment,
  onDeleteEquipment,
  isReadOnly = false,
}: DwellingProfileProps) {
  const codeEdition = project.code_edition ?? defaultEditionForState(project.customer_state);
  const pack = getEditionPack(codeEdition);

  const checkedByLabel = useMemo(() => {
    const map = new Map<string, Equipment>();
    for (const row of existingRows) map.set(row.equipment_name, row);
    return map;
  }, [existingRows]);

  // A checklist item whose row is being removed shows that, instead of
  // offering a quantity that would add a second row under the same name.
  const removedByLabel = useMemo(() => {
    const map = new Map<string, Equipment>();
    for (const row of removedRows) map.set(row.equipment_name, row);
    return map;
  }, [removedRows]);

  const setItemQuantity = (item: DwellingChecklistItem, qty: number) => {
    if (isReadOnly) return;
    const current = rowForChecklistItem(item, checkedByLabel);
    if (qty <= 0) {
      if (current) onDeleteEquipment(current.id);
      return;
    }
    if (!current) {
      onAddExisting({
        equipment_name: item.label,
        equipment_type: item.equipmentType,
        kw: item.defaultKw,
        quantity: qty,
        value_source: item.defaultKw === null ? 'nameplate' : 'default',
        ...item.seed,
      });
    } else if (current.quantity !== qty) {
      onUpdateEquipment(current.id, { quantity: qty });
    }
  };

  const connectedVA =
    (Math.max(0, project.dwelling_floor_area_sqft || 0) +
      Math.max(0, project.added_floor_area_sqft || 0)) *
      pack.lightingVaPerSqFt +
    1500 * Math.max(0, project.small_appliance_circuit_count ?? 2) +
    1500 * Math.max(0, project.laundry_circuit_count ?? 1) +
    existingRows.reduce((sum, eq) => sum + rowVA(eq), 0);

  const groups: DwellingChecklistItem['group'][] = ['kitchen', 'laundry-water', 'hvac', 'other'];

  return (
    <div data-testid="dwelling-profile" className="space-y-4">
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1">
          Dwelling Profile
        </h2>
        <p className="text-sm text-gray-600 mb-4">
          The traditional calculation builds the existing load from the home itself — no
          utility data involved. Demand factors are applied on the Results tab; enter
          connected loads here at their nameplate values.
        </p>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-2">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Floor area (ft²) <span className="text-red-600">*</span>{' '}
              <Tooltip text="Measure by outside dimensions. Exclude open porches, garages, and unfinished spaces not adaptable for future living space." />
            </label>
            <input
              type="number"
              min="0"
              value={project.dwelling_floor_area_sqft ?? ''}
              onChange={(e) =>
                onChange({
                  dwelling_floor_area_sqft: e.target.value ? Number(e.target.value) : null,
                })
              }
              data-testid="dwelling-floor-area"
              className={inputClass}
              disabled={isReadOnly}
              placeholder="e.g. 1800"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Added area (ft²){' '}
              <Tooltip text="Square footage of an addition being built. Counted as its own general-lighting line on the worksheet so the AHJ can see the addition separately." />
            </label>
            <input
              type="number"
              min="0"
              value={project.added_floor_area_sqft ?? ''}
              onChange={(e) =>
                onChange({
                  // Clamped, not just min="0": the column has a >= 0 CHECK, and a
                  // typed negative reached Postgres and failed the cloud save with
                  // a message that named neither the field nor the reason.
                  added_floor_area_sqft: clampInput(e.target.value, 0),
                })
              }
              data-testid="dwelling-added-area"
              className={inputClass}
              disabled={isReadOnly}
              placeholder="0"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Small-appliance circuits{' '}
              <Tooltip text="The 20-amp kitchen/dining receptacle circuits. The code minimum is 2, counted at 1,500 VA each." />
            </label>
            <input
              type="number"
              min="0"
              value={project.small_appliance_circuit_count ?? 2}
              onChange={(e) =>
                onChange({ small_appliance_circuit_count: Math.max(0, Number(e.target.value)) })
              }
              data-testid="small-appliance-circuits"
              className={inputClass}
              disabled={isReadOnly}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Laundry circuits{' '}
              <Tooltip text="Counted at 1,500 VA each. Enter 0 only if the home genuinely has no laundry branch circuit." />
            </label>
            <input
              type="number"
              min="0"
              value={project.laundry_circuit_count ?? 1}
              onChange={(e) =>
                onChange({ laundry_circuit_count: Math.max(0, Number(e.target.value)) })
              }
              data-testid="laundry-circuits"
              className={inputClass}
              disabled={isReadOnly}
            />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 sm:p-6">
        <h3 className="text-base sm:text-lg font-semibold text-gray-900 mb-1">
          Existing fixed appliances & equipment
        </h3>
        <p className="text-sm text-gray-600 mb-4">
          Set a quantity for everything that is fastened in place, permanently connected, or
          on its own circuit. Standard per-unit values are pre-filled — adjust any of them
          from the equipment nameplate in the rows below. Heating and cooling have no
          standard value; enter theirs from the nameplate. Fuel-fired appliances still carry
          small electrical loads — use those variants for gas, oil or propane equipment.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1 mb-4">
          {groups.map((group) => (
            <div key={group} className="mb-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-1.5">
                {CHECKLIST_GROUP_LABELS[group]}
              </p>
              {DWELLING_CHECKLIST.filter((i) => i.group === group).map((item) => {
                const row = rowForChecklistItem(item, checkedByLabel);
                const removedRow = row ? undefined : rowForChecklistItem(item, removedByLabel);
                const qty = row?.quantity ?? removedRow?.quantity ?? 0;
                if (removedRow) {
                  return (
                    <div
                      key={item.key}
                      data-testid={`checklist-removed-${item.key}`}
                      className="flex items-center gap-2.5 py-1 text-sm text-gray-400"
                    >
                      <span className="w-16 px-2 py-1 text-center border border-gray-200 rounded bg-gray-50">
                        {qty}
                      </span>
                      <span className="line-through">{item.label}</span>
                      <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200 no-underline">
                        being removed
                      </span>
                    </div>
                  );
                }
                return (
                  <div
                    key={item.key}
                    className="flex items-center gap-2.5 py-1 text-sm text-gray-800"
                  >
                    <select
                      value={qty}
                      onChange={(e) => setItemQuantity(item, Number(e.target.value))}
                      disabled={isReadOnly}
                      data-testid={`checklist-${item.key}`}
                      aria-label={`${item.label} — quantity`}
                      className="w-16 px-2 py-1 border border-gray-300 rounded text-sm bg-white focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      {quantityOptions(item, qty).map((n) => (
                        <option key={n} value={n}>
                          {n}
                        </option>
                      ))}
                    </select>
                    <span className={qty > 0 ? '' : 'text-gray-500'}>{item.label}</span>
                    {item.defaultKw !== null ? (
                      <span className="text-xs text-gray-400">{item.defaultKw} kW ea.</span>
                    ) : (
                      <span className="text-xs text-amber-600">nameplate</span>
                    )}
                    {item.verifyNameplate && (
                      <Tooltip text="Ratings for this equipment vary widely — check the nameplate rather than relying on the standard value." />
                    )}
                    {item.note && <Tooltip text={item.note} />}
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        {existingRows.length > 0 && (
          <div className="border-t border-gray-100 pt-4 space-y-3">
            {existingRows.map((eq, index) => (
              <div
                key={eq.id}
                data-testid={`existing-row-${index}`}
                className="flex flex-wrap items-end gap-3 bg-gray-50 border border-gray-200 rounded-lg p-3"
              >
                <div className="flex-1 min-w-[160px]">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Name</label>
                  <input
                    type="text"
                    value={eq.equipment_name}
                    onChange={(e) => onUpdateEquipment(eq.id, { equipment_name: e.target.value })}
                    data-testid={`existing-name-${index}`}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="w-16">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Qty</label>
                  <input
                    type="number"
                    min="1"
                    value={eq.quantity}
                    onChange={(e) =>
                      onUpdateEquipment(eq.id, {
                        // Never below 1: the engine counts a 0 as 1 anyway,
                        // and a row showing 0 while counting is misleading.
                        quantity: Math.max(1, Math.floor(Number(e.target.value) || 1)),
                        value_source: 'nameplate',
                      })
                    }
                    data-testid={`existing-quantity-${index}`}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="w-24">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    kW{' '}
                    <span className="text-gray-400">
                      {eq.quantity > 1 ? 'each' : '(or amps)'}
                    </span>
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={eq.kw ?? ''}
                    placeholder="OR"
                    onChange={(e) =>
                      onUpdateEquipment(eq.id, {
                        kw: e.target.value ? Number(e.target.value) : null,
                        amps: e.target.value ? null : eq.amps,
                        value_source: 'nameplate',
                      })
                    }
                    data-testid={`existing-kw-${index}`}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="w-24">
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Amps{eq.quantity > 1 ? <span className="text-gray-400"> each</span> : null}
                  </label>
                  <input
                    type="number"
                    min="0"
                    step="0.1"
                    value={eq.amps ?? ''}
                    placeholder="OR"
                    onChange={(e) =>
                      onUpdateEquipment(eq.id, {
                        amps: e.target.value ? Number(e.target.value) : null,
                        kw: e.target.value ? null : eq.kw,
                        value_source: 'nameplate',
                      })
                    }
                    data-testid={`existing-amps-${index}`}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm"
                    disabled={isReadOnly}
                  />
                </div>
                <div className="w-20">
                  <label className="block text-xs font-medium text-gray-600 mb-1">Voltage</label>
                  <input
                    type="number"
                    value={eq.voltage}
                    onChange={(e) =>
                      onUpdateEquipment(eq.id, { voltage: Number(e.target.value) })
                    }
                    data-testid={`existing-voltage-${index}`}
                    className="w-full px-2 py-1.5 border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 text-sm"
                    disabled={isReadOnly}
                  />
                </div>
                {eq.equipment_type === 'Dryer' && (
                  <label className="flex items-center gap-1.5 pb-1.5 text-xs text-gray-700 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={eq.on_laundry_circuit ?? false}
                      onChange={(e) =>
                        onUpdateEquipment(eq.id, { on_laundry_circuit: e.target.checked })
                      }
                      disabled={isReadOnly}
                      data-testid={`existing-laundry-circuit-${index}`}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
                    />
                    On the laundry circuit
                    <Tooltip text="The code counts a dryer separately only when it is NOT on the laundry branch circuit already counted at 1,500 VA. Most electric dryers have their own dedicated 240 V circuit — leave this unchecked for those." />
                  </label>
                )}
                <div className="flex items-center gap-2 pb-1">
                  {eq.value_source === 'default' && (
                    <span
                      data-testid={`existing-default-tag-${index}`}
                      className="text-xs px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 border border-blue-200"
                      title="Standard value — verify against the equipment nameplate. Defaults in use are disclosed on the report."
                    >
                      default
                    </span>
                  )}
                  {!isReadOnly && onMarkRemoved && (
                    <button
                      type="button"
                      onClick={() => onMarkRemoved(eq.id)}
                      data-testid={`btn-mark-removed-${index}`}
                      className="text-xs text-blue-700 underline hover:text-blue-900 whitespace-nowrap"
                      title="This equipment is being taken out as part of the work. It moves to Equipment Being Removed on the New Loads tab and stops counting here; the report lists it as removed."
                    >
                      Removed with this work
                    </button>
                  )}
                  {!isReadOnly && (
                    <button
                      onClick={() => onDeleteEquipment(eq.id)}
                      data-testid={`btn-delete-existing-${index}`}
                      className="p-1.5 text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Remove"
                    >
                      <Trash2 size={16} />
                    </button>
                  )}
                </div>
                {eq.quantity > 1 && (
                  <div
                    data-testid={`existing-multi-row-${index}`}
                    className="basis-full flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-gray-600"
                  >
                    <span>
                      {eq.quantity} units at the rating above ={' '}
                      <strong>{(rowVA(eq) / 1000).toFixed(1)} kVA</strong> total
                    </span>
                    {!isReadOnly && (
                      <button
                        type="button"
                        onClick={() => onSplitExisting(eq.id)}
                        data-testid={`btn-split-existing-${index}`}
                        className="text-blue-700 underline hover:text-blue-900"
                      >
                        Split into {eq.quantity} separate rows
                      </button>
                    )}
                    <Tooltip text="Keep one row when the units are identical — enter the rating once and it counts that many times. Split them when the units differ, so each gets its own name and rating." />
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {removedRows.length > 0 && (
          <div data-testid="existing-removed-rows" className="border-t border-gray-100 pt-4 mt-3 space-y-2">
            <p className="text-xs text-gray-600">
              <strong>Being removed with this work.</strong> Still listed so the inventory shows the
              house as it is today, but not counted: the traditional calculation describes the house
              after the work. Edit these rows under Equipment Being Removed on the New Loads tab.
            </p>
            {removedRows.map((eq, index) => (
              <div
                key={eq.id}
                data-testid={`existing-removed-row-${index}`}
                className="flex flex-wrap items-center gap-3 bg-gray-50 border border-dashed border-gray-300 rounded-lg p-3 text-sm text-gray-500"
              >
                <span className="flex-1 min-w-[160px] line-through">
                  {eq.equipment_name || 'Unnamed equipment'}
                </span>
                <span className="line-through">
                  {eq.quantity > 1 ? `${eq.quantity} × ` : ''}
                  {eq.kw != null ? `${eq.kw} kW` : eq.amps != null ? `${eq.amps} A @ ${eq.voltage} V` : 'no rating'}
                </span>
                <span className="text-xs px-1.5 py-0.5 rounded bg-amber-50 text-amber-800 border border-amber-200">
                  Removed with this work
                </span>
                {!isReadOnly && onReturnToInventory && (
                  <button
                    type="button"
                    onClick={() => onReturnToInventory(eq.id)}
                    data-testid={`btn-undo-removed-${index}`}
                    className="text-xs text-blue-700 underline hover:text-blue-900"
                  >
                    Undo: keep it
                  </button>
                )}
              </div>
            ))}
          </div>
        )}

        {(() => {
          const heatVA = existingRows
            .filter((r) => r.equipment_type === 'Heat Pump' || r.equipment_type === 'Strip Heat')
            .reduce((s, r) => s + rowVA(r), 0);
          const coolVA = existingRows
            .filter((r) => r.equipment_type === 'Heat Pump' || r.equipment_type === 'A/C')
            .reduce((s, r) => s + rowVA(r), 0);
          if (heatVA === 0 && coolVA === 0) return null;
          return (
            <div
              data-testid="hvac-subtotals"
              className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 bg-gray-50 border border-gray-200 rounded-md px-3 py-2"
            >
              <span>
                Heating side: <strong>{(heatVA / 1000).toFixed(1)} kVA</strong>
              </span>
              <span>
                Cooling side: <strong>{(coolVA / 1000).toFixed(1)} kVA</strong>
              </span>
              <Tooltip text={`A heat pump counts on both sides. Under (B) and the 2026 method the larger side governs; under (A) both sides count unless the ${pack.sectionMap.noncoincident} preparer election below is on. This is where that decision becomes visible.`} />
            </div>
          );
        })()}

        {!isReadOnly && (
          <button
            onClick={() =>
              onAddExisting({ equipment_name: '', equipment_type: 'Other', value_source: 'nameplate' })
            }
            data-testid="btn-add-existing"
            className="mt-3 flex items-center gap-2 px-3 py-2 text-sm text-blue-700 border border-blue-300 rounded-md hover:bg-blue-50 transition-colors"
          >
            <Plus size={16} />
            Add another fixed load
          </button>
        )}

        {/* Both elections belong to the 2020/2023 (A)/(B) fork. The 2026 table
            applies its own larger-of rule and its own EVSE section, so under
            that pack neither choice exists and the block stays out of the way. */}
        {pack.existing83Shape === 'AB' && (
        <div className="mt-4 border-t border-gray-100 pt-4 space-y-2" data-testid="preparer-elections">
          <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
            Preparer elections
          </p>
          <label className="flex items-start gap-2.5 cursor-pointer text-sm text-gray-800">
            <input
              type="checkbox"
              checked={project.noncoincident_election ?? false}
              onChange={(e) => onChange({ noncoincident_election: e.target.checked })}
              disabled={isReadOnly}
              data-testid="noncoincident-election"
              className="w-5 h-5 mt-0.5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <span>
              Count only the larger of heating vs. cooling under (A) — preparer election per NEC{' '}
              {pack.sectionMap.noncoincident}
              <Tooltip text={`${pack.sectionMap.existingDwelling}(A) itself grants no larger-of exclusion — by default both existing heating and cooling count at nameplate. Enabling this applies the noncoincident-load principle for an AHJ who accepts it, and the report names it as a preparer election, not a section step. (B) applies its own larger-of rule regardless.`} />
            </span>
          </label>
          {pack.evseRule === 'max7200' && (
            <label className="flex items-start gap-2.5 cursor-pointer text-sm text-gray-800">
              <input
                type="checkbox"
                checked={project.apply_evse_floor ?? true}
                onChange={(e) => onChange({ apply_evse_floor: e.target.checked })}
                disabled={isReadOnly}
                data-testid="evse-floor-election"
                className="w-5 h-5 mt-0.5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
              />
              <span>
                Apply the 7,200 VA per-EVSE minimum (imported from NEC {pack.sectionMap.evse})
                <Tooltip text={`This floor comes from the standard method's EVSE section, which ${pack.sectionMap.existingDwelling} does not reference — a conservative election, not a requirement. Unchecking it counts EVSE at nameplate as the section calls for. Either way the report states the treatment.`} />
              </span>
            </label>
          )}
        </div>
        )}

        <div className="mt-4 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-t border-gray-100 pt-4">
          <label className="flex items-start gap-2.5 cursor-pointer">
            <input
              type="checkbox"
              checked={project.inventory_attested ?? false}
              onChange={(e) => onChange({ inventory_attested: e.target.checked })}
              disabled={isReadOnly}
              data-testid="inventory-attested"
              className="w-5 h-5 mt-0.5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500 disabled:opacity-50"
            />
            <span className="text-sm text-gray-800">
              All appliances that are fastened in place, permanently connected, or on a
              dedicated circuit are listed above. <span className="text-red-600">*</span>
              <Tooltip text="The traditional method is only as good as the inventory. This attestation is printed on the report above the signature block." />
            </span>
          </label>
          <div className="text-sm text-gray-600 whitespace-nowrap" data-testid="connected-subtotal">
            Connected load entered:{' '}
            <strong>{(connectedVA / 1000).toFixed(1)} kVA</strong>
          </div>
        </div>
      </div>
    </div>
  );
}
